/**
 * A row's words in each offer language (D-303), as Core keeps them: `values` in the base language
 * (the first of the company's offer languages) with the numbers, `translations` the words in the
 * others — { en: { name: '…', unit: '…' } }.
 */
export const offerLanguages = (meta) => {
  const list = meta?.data?.settings?.offer_languages
  return Array.isArray(list) && list.length ? list : ['pl', 'en']
}
export const baseLanguage = (meta) => offerLanguages(meta)[0]
export const otherLanguages = (meta) => offerLanguages(meta).slice(1)

/** Whether a value is a word (to be translated), not a number. */
export const isWord = (v) => typeof v === 'string' && v.trim() !== '' && Number.isNaN(Number(v))

/** A row's word in a language: the base one from its values, another from its translations (none: ''). */
export function wordIn(row, key, lang, base) {
  const v = lang === base ? row?.values?.[key] : row?.translations?.[lang]?.[key]
  return typeof v === 'string' ? v : ''
}

/** The row with one word set in a language: into its values (base) or its translations. */
export function withWord(row, key, lang, base, text) {
  if (lang === base) return { ...row, values: { ...(row.values || {}), [key]: text } }
  const translations = JSON.parse(JSON.stringify(row.translations && typeof row.translations === 'object' ? row.translations : {}))
  if (String(text ?? '').trim() === '') {
    if (translations[lang]) delete translations[lang][key]
    if (translations[lang] && !Object.keys(translations[lang]).length) delete translations[lang]
  } else translations[lang] = { ...(translations[lang] || {}), [key]: text }
  return { ...row, translations }
}

/**
 * The base words copied in place of a translation (Core's TranslationCheck::copied, D-304): the same
 * words, two or more, one an ordinary word in small letters. A name the same in both is no copy.
 */
export function copied(base, other) {
  const a = String(base ?? '').replace(/\s+/g, ' ').trim()
  if (!a || a !== String(other ?? '').replace(/\s+/g, ' ').trim()) return false
  const words = a.replace(/\{\{[^}]*\}\}/g, ' ').split(/[^\p{L}]+/u).filter(Boolean)
  // An ordinary word in small letters among them: a name the same in both (capitalised) is no copy.
  return words.length >= 2 && words.some((w) => w === w.toLowerCase() && (w.length >= 3 || ['i', 'na', 'do', 'w', 'z', 'od', 'po', 'za'].includes(w)))
}

/** The words a row has in the base language and not in `lang` (none, or the base ones copied). */
export function missingWords(row, lang, base) {
  if (lang === base) return []
  return Object.entries(row?.values || {}).filter(([, v]) => isWord(v)).map(([k]) => k).filter((k) => {
    const other = row?.translations?.[lang]?.[k]
    return !String(other ?? '').trim() || copied(row.values[k], other)
  })
}
